import { v4 as uuid } from "uuid";

/**
 * @typedef {object} TemplateFieldSpec
 * @property {string} Title - Field label shown on the form.
 * @property {string} Type - Field type (`text` | `number` | `multiple-one` | `multiple-many`).
 * @property {string} Required - `"true"` or `"false"` — stored as a string, like the other templates.
 * @property {Array<string>} [Options] - Answer options for the multiple-* types.
 */

/**
 * @typedef {object} EnergyPovertyTemplateInput
 * @property {string} client - Tenant CompagnyID to stamp on every document.
 * @property {*} timestamp - Firestore Timestamp value for `Timestamp`.
 * @property {string} [userId] - ID of the user creating the form.
 * @property {number} position - `Position` for the new form (usually list length + 1).
 * @property {() => string} [idFn] - ID generator, injectable for tests (defaults to uuid).
 */

/**
 * @typedef {object} EnergyPovertyTemplateResult
 * @property {object} form - New `IntakeForms` doc data.
 * @property {Array<object>} fields - New `IntakeFormFields` docs.
 * @property {Array<object>} options - New `MultipleQuestionOptions` docs.
 */

/**
 * `Postcode`, `Huisnummer` and `Straatnaam` are standardized titles (see
 * helpers/standardizedDataFields.js) — the answers only land on the household
 * record when the field is titled exactly like this, and IntakeFormOpen's PDOK
 * autofill matches on the same titles.
 *
 * `Type woning` is standardized too AND carries an auto-rule in
 * IntakeFormBuilder: touching the field re-injects koop / sociale huur /
 * particuliere huur. So the template seeds that exact set, and the building
 * type lives in its own `Woningsoort` field — mixing both into one
 * single-answer question is what the auto-rule would otherwise produce.
 *
 * @type {Array<TemplateFieldSpec>}
 */
const TEMPLATE_FIELDS = [
  { Title: "Postcode", Type: "text", Required: "true" },
  { Title: "Huisnummer", Type: "number", Required: "true" },
  // A numeric Huisnummer cannot hold "12A", so the letter/addition gets
  // its own text field. The PDOK lookup reads it: in a building with 12A
  // and 12B it picks the unit, and so the BAG-id (verifyAddressMatch).
  { Title: "Toevoeging", Type: "text", Required: "false" },
  // These sit after postcode + huisnummer on purpose: IntakeFormOpen looks the
  // address up in PDOK once those two are filled and writes the result into
  // both fields. buildNewAdressTitle then joins Straatnaam + Huisnummer into
  // the household title — without Straatnaam the title is just "Nieuw adres".
  { Title: "Straatnaam", Type: "text", Required: "true" },
  { Title: "Woonplaats", Type: "text", Required: "false" },
  // Deliberately not a checkbox: an untouched checkbox is stored as "false",
  // which would make 'geweigerd' indistinguishable from 'niet ingevuld'.
  {
    Title: "Hulp geaccepteerd of geweigerd",
    Type: "multiple-one",
    Required: "false",
    Options: ["geaccepteerd", "geweigerd", "nog onbekend"],
  },
  {
    Title: "Type woning",
    Type: "multiple-one",
    Required: "false",
    Options: ["koop", "sociale huur", "particuliere huur"],
  },
  {
    Title: "Woningsoort",
    Type: "multiple-one",
    Required: "false",
    Options: [
      "rijtjeshuis",
      "twee-onder-een-kapwoning",
      "vrijstaande woning",
      "hoekwoning",
      "appartement",
      "portiekflat",
      "galerijflat",
    ],
  },
  {
    Title: "Zichtbare problemen in de woning",
    Type: "multiple-many",
    Required: "false",
    Options: [
      "schimmel",
      "vocht",
      "tocht bij ramen of deuren",
      "enkel glas",
      "slecht sluitende ramen of deuren",
      "geen of kapotte ventilatie",
      "koude of ongeisoleerde vloer",
      "ongeisoleerd dak",
      "ongeisoleerde spouwmuur",
      "verouderde cv-ketel of kachel",
      "condens op de ramen",
      "geen problemen zichtbaar",
    ],
  },
  // Also not a checkbox: a checkbox answer is persisted as the string
  // "true"/"false", which is what the household data view shows verbatim.
  // Optional: at intake a referral has often not been decided yet, so forcing
  // an answer would push the invuller into a false "Nee".
  {
    Title: "Bewoners doorverwezen naar andere organisatie",
    Type: "multiple-one",
    Required: "false",
    Options: ["Ja", "Nee"],
  },
  // Totals for the effort spent on this household. Optional: at intake these
  // are usually still unknown — they get filled in later, from the
  // household's Gegevens tab.
  { Title: "Totaal aantal bezoeken", Type: "number", Required: "false" },
  { Title: "Totaal bestede uren", Type: "number", Required: "false" },
];

/**
 * Build every document for the "Impact meten Energiearmoede" intake template.
 *
 * Pure: performs no Firestore writes. The caller commits the returned docs.
 *
 * @param {EnergyPovertyTemplateInput} input
 * @returns {EnergyPovertyTemplateResult}
 */
const buildEnergyPovertyIntakeTemplate = ({
  client,
  timestamp,
  userId = "",
  position,
  idFn = uuid,
}) => {
  const formID = idFn();

  const form = {
    ID: formID,
    Title: "Template: Impact meten Energiearmoede",
    CompagnyID: client,
    Compagny: client,
    Position: position,
    Timestamp: timestamp,
    User: userId,
    DataItems: [],
    FormType: "intake",
    FormTarget: "adress",
    FormSelected: "",
  };

  const fields = [];
  const options = [];

  TEMPLATE_FIELDS.forEach((spec, index) => {
    const fieldID = idFn();

    fields.push({
      ID: fieldID,
      Title: spec.Title,
      Type: spec.Type,
      Required: spec.Required,
      Position: index + 1,
      FormId: formID,
      CompagnyID: client,
      Compagny: client,
      Timestamp: timestamp,
    });

    (spec.Options || []).forEach((label, optionIndex) => {
      options.push({
        ID: idFn(),
        Option: label,
        Question: spec.Title,
        Field: fieldID,
        CompagnyID: client,
        Compagny: client,
        Timestamp: timestamp,
        Position: optionIndex + 1,
        // MultipleQuestionOptions keeps Deleted as a string — countActiveOptions
        // compares Deleted === "true".
        Deleted: "false",
        DeletedAt: "",
      });
    });
  });

  return { form, fields, options };
};

export default buildEnergyPovertyIntakeTemplate;
